<template>
    <div class="section-video" :class="color">
        <layout-contained :class="padding">
            <embed-youtube
                :video="video"
                :autoplay="true"
                @play="onVideoPlay"
                @progress="onVideoProgress"
                @pause="onVideoPause"
                @destroy="onVideoDestroy"
                @end="onVideoEnd"
            ></embed-youtube>
            <content-heading
                v-if="heading"
                class="mt-4 sm:mt-6"
                :heading="heading"
                :headingColor="headingColor"
                :headingComponent="headingComponent"
                :textCentered="textCentered"
                marginBottom="mb-2"
            ></content-heading>
            <content-text
                v-if="content"
                :html="content"
                :contentColor="contentColor"
                :textCentered="textCentered"
            ></content-text>
            <slot name="custom"></slot>
        </layout-contained>
    </div>
</template>

<script>
export default {
    name: 'SectionVideo',
    props: {
        video: {
            required: true,
            type: Object,
            default() {
                return {};
            },
        },
        padding: {
            required: false,
            default: 'py-5 md:py-8 lg:py-9',
            type: String,
        },
        color: {
            required: false,
            type: String,
            default: 'bg-black',
        },
        contentColor: {
            required: false,
            type: String,
            default: 'text-zinc-100',
        },
        headingColor: {
            required: false,
            type: String,
            default: 'text-zinc-50',
        },
        headingComponent: {
            required: false,
            type: String,
            default: 'h1',
        },
        textCentered: {
            required: false,
            type: [Boolean, Number],
            default: false,
        },
    },
    data() {
        return {
            ended: false,
        };
    },
    computed: {
        heading() {
            return this.video.video_title;
        },
        content() {
            let details = '';
            if (this.video.video_dancers) {
                details += '<p class="mb-3! text-xl!">';
                let dancerNames = this.video.video_dancer_names.split(' & ');
                this.video.video_dancers.split(';').forEach((dancerSlug, index) => {
                    if (details !== '<p class="mb-3! text-xl!">') details += ' & ';
                    details +=
                        '<router-link class="text-sky-500! font-medium! no-underline! hover:underline!"' +
                        ' to="/dancers/' +
                        dancerSlug +
                        '">' +
                        dancerNames[index] +
                        '</router-link>';
                });
                details += '</p>';
            }
            if (
                this.video.video_description !==
                'View this video on the ' + this.$config.public.siteName + ' website.'
            ) {
                details += '<p class="video-description">' + this.video.video_description + '</p>';
            }
            if (this.video?.video_operator === '@triplesteptv') {
                details +=
                    '<p class="video-description">Video by <a class="text-sky-500! font-medium! no-underline! hover:underline!" href="https://www.triplestep.tv/">Triple Step TV</a></p>';
            }
            if (['1st', '2nd', '3rd', '4th', '5th'].indexOf(this.video.video_placing) !== -1) {
                details += '<p class="video-details">🏆 &nbsp;' + this.video.video_placing + ' Place</p>';
            }
            if (this.video.video_division && this.video.video_format) {
                details +=
                    '<p class="video-details">🎬 &nbsp;' +
                    this.video.video_division +
                    ' - ' +
                    this.video.video_format +
                    '</p>';
            }
            if (this.video.event_slug && this.video.event_title) {
                details += '<p class="video-details">📍 &nbsp;' + this.video.event_title + '</p>';
            }
            if (this.video.video_date_taken_long) {
                details +=
                    '<p class="video-details mt-4 text-sm! text-zinc-400! italic">' +
                    this.video.video_date_taken_long +
                    '</p>';
            }
            return details;
        },
    },
    methods: {
        patchVideo(event) {
            useEntityVideo().patch(this.video.id_video, {
                id_video_play: this.video.id_video_play,
                video_play_total_time: (event.totalTime * 1000).toFixed(0),
                video_play_current_time: (event.currentTime * 1000).toFixed(0),
            });
        },
        onVideoPlay(event) {
            if (this.$config.public.stage === 'dev') console.log('Video play:', event);
            this.patchVideo(event);
        },
        onVideoProgress(event) {
            // if (this.$config.public.stage === 'dev') console.log('Video progress:', event);
            if (event.totalTime % 5 === 0) this.patchVideo(event);
        },
        onVideoPause(event) {
            if (this.$config.public.stage === 'dev') console.log('Video pause:', event);
            this.patchVideo(event);
        },
        onVideoDestroy(event) {
            if (this.$config.public.stage === 'dev') console.log('Video destroy:', event);
            this.patchVideo(event);
        },
        onVideoEnd(event) {
            if (this.$config.public.stage === 'dev') console.log('Video end:', event);
            this.patchVideo(event);
            this.ended = true;
        },
    },
};
</script>

<style>
@reference "../../assets/main.css";

.content-text .video-description {
    @apply mb-3;
    @apply text-zinc-300;
}
.content-text .video-details {
    @apply mb-2;
}
.content-text .video-details a {
    @apply text-white;
    @apply no-underline;
}
.content-text .video-details a:hover {
    @apply underline;
}
</style>
